import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useState, useEffect } from "react";
import Header from "../../../../components/Header";
import {
  fetchJson,
  executeAnyMutation,
} from "../../../../data/DynamicServiceNew/jsondynamicservice";
import { setupManagementUrl, customerservice } from "../../../../config";
import Swal from "sweetalert2";
import { loanManagementUrl } from "../../../../config";

const setup_url = setupManagementUrl.uri;
// Round to 2dp without floating point noise (e.g. 33.330000000000005 -> 33.33)
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const parseDecimal = (value) => {
  const parsed = parseFloat(value);
  return Number.isNaN(parsed) ? 0 : round2(parsed);
};

const CommissionForm = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));

  // ---- Customer search state ----
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [filteredCustomer, setFilteredCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(
    props.data
      ? { Id: props.data.CustomerId, Name: props.data.CustomerName }
      : null
  );

  // ---- Product (derived from the selected loan, not user-editable) ----
  const [productName, setProductName] = useState(
    props.data ? props.data.ProductName : ""
  );

  // ---- Loans (dependent on selected customer) ----
  const [loans, setLoans] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(false);

  // ---- Products (for commission-limit verification & display name) ----
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  // Holds { MinCommission, MaxCommission } for whatever product is
  // currently attached to the selected loan. Null = nothing to show/check yet.
  const [productLimits, setProductLimits] = useState(null);

  // ---- Base amount the commission percentage is calculated against ----
  // This is the selected loan's AmountToDisburse. Null/0 = no loan
  // selected yet, so percentage <-> amount syncing is disabled.
  const [loanBaseAmount, setLoanBaseAmount] = useState(
    props.data ? props.data.AmountToDisburse ?? null : null
  );

   // Keep local UI state in sync with props.data (handles async data & re-initialization)
  useEffect(() => {
    if (props.isEditing && props.data) {
      setSelectedCustomer({
        Id: props.data.CustomerId,
        Name: props.data.CustomerName,
      });
      setProductName(props.data.ProductName || "");
      setLoanBaseAmount(props.data.AmountToDisburse ?? null);
    }
  }, [props.isEditing, props.data?.Id]);

  // Preload loans once the customer ID is available
  useEffect(() => {
    if (props.isEditing && props.data?.CustomerId) {
      fetchLoansForCustomer(props.data.CustomerId);
    }
  }, [props.isEditing, props.data?.CustomerId]);

  // Fetch the product catalogue (with commission limits) once on mount
  useEffect(() => {
    fetchProducts();
  }, []);

  // Once BOTH loans and products have loaded for an existing commission,
  // resolve the product name for display. The loan record only carries
  // ProductId — never a ProductName/Product field — so the name has to
  // be looked up from the product catalogue by matching ProductId -> Id.
  // Also backfill loanBaseAmount (AmountToDisburse) from the matched loan
  // in case it wasn't already present on props.data.
  useEffect(() => {
    if (
      props.isEditing &&
      props.data?.LoanId &&
      loans.length > 0 &&
      products.length > 0 &&
      !productName
    ) {
      const matchedLoan = loans.find((loan) => loan.Id === props.data.LoanId);
      if (matchedLoan) {
        const matchedProduct = products.find(
          (p) => Number(p.Id) === Number(matchedLoan.ProductId)
        );
        if (matchedProduct) {
          setProductName(matchedProduct.Name || "");
        }
        if (loanBaseAmount == null) {
          setLoanBaseAmount(matchedLoan.AmountToDisburse ?? null);
        }
      }
    }
  }, [loans, products]);

useEffect(() => {
  console.log("props.data", props.data);
  console.log("products", products);

  if (props.isEditing && props.data?.ProductId && products.length > 0) {
    console.log("Looking for ProductId:", props.data.ProductId);

    const matchedProduct = products.find((p) => {
      console.log("Comparing", p.Id, "with", props.data.ProductId);
      return Number(p.Id) === Number(props.data.ProductId);
    });

    console.log("Matched Product:", matchedProduct);

    if (matchedProduct) {
      setProductLimits({
        MinCommission: matchedProduct.MinCommission,
        MaxCommission: matchedProduct.MaxCommission,
      });
    }
  }
}, [products, props.data, props.isEditing]);

  const fetchProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await fetchJson({
        objectName: "allProductsByColumnValues",
        inputObjectName: "parameters",
        params: { companyId: parseInt(decodedToken?.CompanyId) },
        baseUrl: setup_url,
      });

      const results = JSON.parse(response.results) || [];
      setProducts(results);
    } catch (error) {
      console.error("Error fetching products:", error);
      Swal.fire(
        "Product Lookup Failed",
        "Could not retrieve products for commission verification.",
        "error"
      );
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchLoansForCustomer = async (customerId) => {
    setLoadingLoans(true);
    try {
      const response = await fetchJson({
        objectName: "loanApplicationByCustomer",
        inputObjectName: "loans",
        params: { customerId: customerId },
        baseUrl: loanManagementUrl.uri,
      });

      const results = JSON.parse(response.results) || [];
      setLoans(results);
    } catch (error) {
      console.error("Error fetching loans:", error);
      Swal.fire(
        "Loan Lookup Failed",
        "Could not retrieve active loans for this customer.",
        "error"
      );
    } finally {
      setLoadingLoans(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setLoadingSearch(true);

    try {
      const mutationdata = { searchParameter: searchTerm };

      const customer = await fetchJson({
        objectName: "searchCustomerForLoanRebooking",
        inputObjectName: "searchCustomerforLoanRebooking",
        params: mutationdata,
        baseUrl: customerservice.uri,
      });

      const result = JSON.parse(customer.results);

      if (!result) {
        Swal.fire("Customer Not Found", "Customer does not exist", "warning");
        setFilteredCustomer(null);
      } else {
        const rawList = Array.isArray(result) ? result : [result];

        // Normalize varying API field names to a consistent shape
        const normalized = rawList.map((c) => ({
          Id: c.customer_id ?? c.Id,
          Name: c.customer_name ?? c.Name ?? c.FullName,
          PhoneNumber: c.mobile_number ?? c.PhoneNumber,
          raw: c, // keep the original around in case you need other fields later
        }));

        setFilteredCustomer(normalized);
      }
    } catch (error) {
      console.error("Search error:", error);
      Swal.fire(
        "Search Failed",
        "An error occurred during the search. Please try again later.",
        "error"
      );
    } finally {
      setLoadingSearch(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSelectCustomer = (customer, setFieldValue) => {
    setSelectedCustomer(customer);
    setFilteredCustomer(null);
    setSearchTerm("");
    setFieldValue("customerId", customer.Id);
    setFieldValue("loanId", ""); // reset loan since customer changed
    setFieldValue("productId", ""); // reset product since it's derived from the loan
    setFieldValue("commissionPercentage", ""); // reset — base amount is changing
    setFieldValue("commissionAmount", "");
    setProductName("");
    setProductLimits(null); // reset commission limits since product changed
    setLoanBaseAmount(null); // reset base amount since loan/customer changed
    setLoans([]);
    fetchLoansForCustomer(customer.Id);
  };

  // When a loan is picked, pull its product straight off the loan record
  // and lock it in — the user never chooses a product directly.
 const handleLoanChange = (e, setFieldValue) => {
  const loanId = Number(e.target.value);
  setFieldValue("loanId", loanId);

  const selectedLoan = loans.find(
    (loan) => Number(loan.Id) === loanId
  );

  if (selectedLoan) {
    const loanProductId = Number(selectedLoan.ProductId ?? 0);

    setFieldValue("productId", loanProductId);

    console.log("Loan ProductId:", loanProductId);
    console.log("Products:", products);

    const matchedProduct = products.find(
      (p) => Number(p.Id) === loanProductId
    );

    console.log("Matched Product:", matchedProduct);

    if (matchedProduct) {
      setProductName(matchedProduct.Name || "");

      setProductLimits({
        MinCommission: matchedProduct.MinCommission,
        MaxCommission: matchedProduct.MaxCommission,
      });
    } else {
      setProductName("");
      setProductLimits(null);
    }

    setLoanBaseAmount(selectedLoan.AmountToDisburse ?? null);
    setFieldValue("commissionPercentage", "");
    setFieldValue("commissionAmount", "");
  } else {
    setFieldValue("productId", "");
    setProductName("");
    setProductLimits(null);
    setLoanBaseAmount(null);
    setFieldValue("commissionPercentage", "");
    setFieldValue("commissionAmount", "");
  }
};

  // ---- Two-way sync between commissionPercentage and commissionAmount ----
  // Both are calculated off the selected loan's AmountToDisburse
  // (loanBaseAmount). Editing one recalculates the other; if no loan/base
  // amount is set yet, the fields just behave like plain inputs.

  const handlePercentageChange = (e, setFieldValue) => {
    const rawValue = e.target.value;
    setFieldValue("commissionPercentage", rawValue);

    const percentage = parseFloat(rawValue);
    if (!loanBaseAmount || Number.isNaN(percentage)) {
      if (rawValue === "") setFieldValue("commissionAmount", "");
      return;
    }

    const amount = round2((percentage / 100) * loanBaseAmount);
    setFieldValue("commissionAmount", amount);
  };

  const handleAmountChange = (e, setFieldValue) => {
    const rawValue = e.target.value;
    setFieldValue("commissionAmount", rawValue);

    const amount = parseFloat(rawValue);
    if (!loanBaseAmount || Number.isNaN(amount)) {
      if (rawValue === "") setFieldValue("commissionPercentage", "");
      return;
    }

    const percentage = round2((amount / loanBaseAmount) * 100);
    setFieldValue("commissionPercentage", percentage);
  };

  const initialValues = {
    customerId: props.data ? props.data.CustomerId : "",
    productId: props.data ? props.data.ProductId : "",
    loanId: props.data ? props.data.LoanId : "",
    commissionPercentage: props.data ? props.data.CommissionPercentage : "",
    commissionAmount: props.data ? props.data.CommissionAmount : "",
  };

  // NOTE: productLimits is read via closure here. Since this schema is
  // rebuilt on every render (it's declared inside the component body),
  // Formik always validates against whatever limits are currently loaded.
  const validationSchema = yup.object().shape({
    customerId: yup.string().required("Customer is required"),
    productId: yup.string().required("Product is required"),
    loanId: yup.string().required("Loan is required"),
    commissionPercentage: yup
      .number()
      .typeError("Commission percentage must be a number")
      .min(0, "Cannot be negative")
      .max(100, "Cannot exceed 100%")
      .required("Commission percentage is required")
      .test(
        "within-product-commission-range",
        "Commission percentage is outside the allowed range for this product",
        function (value) {
          if (value == null || Number.isNaN(value)) return true; // let other rules handle it
          if (!productLimits) return true; // nothing to verify against yet

          const { MinCommission, MaxCommission } = productLimits;

          if (MinCommission != null && value < MinCommission) {
            return this.createError({
              message: `Commission percentage cannot be less than ${MinCommission}%`,
            });
          }
          if (MaxCommission != null && value > MaxCommission) {
            return this.createError({
              message: `Commission percentage cannot exceed ${MaxCommission}%`,
            });
          }
          return true;
        }
      ),
    commissionAmount: yup
      .number()
      .typeError("Commission amount must be a number")
      .min(0, "Cannot be negative")
      .required("Commission amount is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // MinCommission / MaxCommission are verification-only and are
      // intentionally excluded from the payload.
      const payload = {
        customerId: values.customerId,
        productId: values.productId,
        loanId: values.loanId,
        commissionPercentage: parseDecimal(values.commissionPercentage),
        commissionAmount: parseDecimal(values.commissionAmount),
        companyId: parseInt(decodedToken?.CompanyId),
        regionId: parseInt(decodedToken?.RegionId),
        branchId: parseInt(decodedToken?.BranchId),
        isActive: true,
      };

      const variables = props.isEditing
        ? { id: props.data.Id, ...payload }
        : payload;

      const response = await executeAnyMutation(
        props.isEditing ? "updateCommission" : "createCommission",
        variables,
        "result",
        false,
        setup_url
      );

      if (response) {
        props.onClose();
        props.onAction();

        await Swal.fire(
          "Success!",
          `Commission has been ${
            props.isEditing ? "updated" : "created"
          } successfully`,
          "success"
        );
      }
    } catch (error) {
      console.error("Error:", error.message);
      Swal.fire(
        "Error",
        "Something went wrong while saving the commission.",
        "error"
      );
    }

    setSubmitting(false);
  };

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Commission" : "Create Commission"}
        subtitle={
          props.isEditing
            ? "Edit an Existing Commission"
            : "Create a New Commission"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="30px">
              {/* Customer search */}
              <Box sx={{ gridColumn: "span 2" }}>
                <Box display="flex" gap="10px" alignItems="flex-start">
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Search Customer*"
                    value={
                      selectedCustomer
                        ? selectedCustomer.Name || selectedCustomer.FullName
                        : searchTerm
                    }
                    onChange={(e) => {
                      setSelectedCustomer(null);
                      setFieldValue("customerId", "");
                      handleSearchChange(e);
                    }}
                    error={touched.customerId && !!errors.customerId}
                    helperText={touched.customerId && errors.customerId}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    disabled={loadingSearch || !searchTerm.trim()}
                    sx={{ height: "56px", mt: "8px" }}
                  >
                    {loadingSearch ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      "Search"
                    )}
                  </Button>
                </Box>

                {filteredCustomer && filteredCustomer.length > 0 && (
                  <Paper sx={{ mt: 1, maxHeight: 200, overflowY: "auto" }}>
                    <List dense>
                      {filteredCustomer.map((customer) => (
                        <ListItemButton
                          key={customer.Id}
                          onClick={() =>
                            handleSelectCustomer(customer, setFieldValue)
                          }
                        >
                          <ListItemText
                            primary={customer.Name || customer.FullName}
                            secondary={customer.PhoneNumber || customer.Id}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>

              {/* Loan (depends on selected customer) */}
              <FormControl
                fullWidth
                variant="filled"
                error={touched.loanId && !!errors.loanId}
                disabled={!values.customerId || loadingLoans}
              >
                <InputLabel id="loanId-label">
                  {loadingLoans ? "Loading Loans..." : "Active Loan*"}
                </InputLabel>
                <Select
                  labelId="loanId-label"
                  id="loanId"
                  name="loanId"
                  value={values.loanId}
                  onChange={(e) => handleLoanChange(e, setFieldValue)}
                  onBlur={handleBlur}
                >
                  <MenuItem value="">
                    <em>
                      {values.customerId
                        ? "Select Loan"
                        : "Select a customer first"}
                    </em>
                  </MenuItem>
                  {loans.map((loan) => (
                    <MenuItem key={loan.Id} value={loan.Id}>
                      LoanId: {loan.LoanNumber || loan.Id} —{" "}
                      Loan Amount: {loan.LoanAmount ?? loan.Amount}
                    </MenuItem>
                  ))}
                </Select>
                {touched.loanId && errors.loanId && (
                  <div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
                    {errors.loanId}
                  </div>
                )}
              </FormControl>

              {/* Product - read-only, set automatically from the selected loan */}
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Product"
                value={productName || (values.loanId ? "" : "Select a loan first")}
                disabled
                InputProps={{ readOnly: true }}
                error={touched.loanId && !!errors.productId}
                helperText={
                  touched.loanId && errors.productId ? errors.productId : ""
                }
              />

              {/*
                Min/Max commission — shown ONLY once a loan/product has
                been resolved and its limits found. Purely informational:
                used to eyeball the entered values against the product's
                allowed range, never sent to the backend on submit.
              */}
              {values.loanId && productLimits && (
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap="20px">
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Min Commission (%)"
                    value={
                      productLimits.MinCommission != null
                        ? productLimits.MinCommission
                        : "N/A"
                    }
                    disabled
                    InputProps={{ readOnly: true }}
                    helperText="For verification only — not submitted"
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Max Commission (%)"
                    value={
                      productLimits.MaxCommission != null
                        ? productLimits.MaxCommission
                        : "N/A"
                    }
                    disabled
                    InputProps={{ readOnly: true }}
                    helperText="For verification only — not submitted"
                  />
                </Box>
              )}

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Commission Percentage*"
                name="commissionPercentage"
                onBlur={handleBlur}
                onChange={(e) => handlePercentageChange(e, setFieldValue)}
                value={values.commissionPercentage}
                disabled={!values.loanId}
                error={
                  touched.commissionPercentage &&
                  !!errors.commissionPercentage
                }
                helperText={
                  (touched.commissionPercentage &&
                    errors.commissionPercentage) ||
                  (values.loanId
                    ? "Adjusts Commission Amount automatically"
                    : "Select a loan first")
                }
                inputProps={{ step: "0.01", min: 0, max: 100 }}
              />

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Commission Amount*"
                name="commissionAmount"
                onBlur={handleBlur}
                onChange={(e) => handleAmountChange(e, setFieldValue)}
                value={values.commissionAmount}
                disabled={!values.loanId}
                error={touched.commissionAmount && !!errors.commissionAmount}
                helperText={
                  (touched.commissionAmount && errors.commissionAmount) ||
                  (values.loanId
                    ? "Adjusts Commission Percentage automatically"
                    : "Select a loan first")
                }
                inputProps={{ step: "0.01", min: 0 }}
              />
            </Box>

            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CommissionForm;